import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import CookieBanner from '../../components/feature/CookieBanner';
import BackToTop from '../../components/feature/BackToTop';
import { injectSchema, getBreadcrumbSchema, getWebPageSchema, getOrganizationSchema } from '../../utils/seoSchema';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'Sobre Nosotros - Empresa de Limpieza Post Incendio Barcelona | FIRETRAUMAS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Empresa especializada en limpieza post incendio en Barcelona. Experiencia local, protocolos seguros y transparencia. Conoce nuestro equipo y valores.');
    }

    // Inject Schema.org JSON-LD
    const cleanupOrganization = injectSchema(getOrganizationSchema());
    const cleanupBreadcrumb = injectSchema(getBreadcrumbSchema([
      { name: 'Inicio', url: '/' },
      { name: 'Sobre Nosotros', url: '/empresa' }
    ]));
    const cleanupWebPage = injectSchema(getWebPageSchema(
      'Sobre Nosotros - Empresa de Limpieza Post Incendio Barcelona',
      'Empresa especializada en limpieza post incendio en Barcelona. Experiencia local, protocolos seguros y transparencia. Conoce nuestro equipo y valores.',
      '/empresa'
    ));

    return () => {
      cleanupOrganization?.();
      cleanupBreadcrumb?.();
      cleanupWebPage?.();
    };
  }, []);

  const values = [
    {
      icon: 'ri-map-pin-user-line',
      title: 'Experiencia Local',
      description: 'Conocemos Barcelona, sus edificios, normativas municipales y aseguradoras. Nuestra experiencia local nos permite actuar con rapidez y eficacia en cada distrito.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Protocolos Seguros',
      description: 'EPIs certificados, contención profesional y aspirado HEPA. Protegemos a nuestro equipo y tu espacio siguiendo los más altos estándares de seguridad.'
    },
    {
      icon: 'ri-eye-line',
      title: 'Transparencia Total',
      description: 'Presupuestos claros sin sorpresas. Coordinación constante contigo y con tu aseguradora. Sabrás exactamente qué hacemos en cada momento.'
    }
  ];

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              {/* Left Content */}
              <div className="lg:col-span-2">
                <span className="text-secondary uppercase text-xs tracking-wider font-sans font-semibold">
                  /quiénes-somos
                </span>
                <h1 className="font-serif font-bold text-5xl md:text-6xl text-primary mt-4 mb-6 leading-tight">
                  Expertos en recuperación de espacios tras incendios
                </h1>
                <p className="text-textLight text-lg leading-relaxed mb-6">
                  FIRETRAUMAS nace de la necesidad de ofrecer un servicio profesional, transparente y humano en momentos difíciles. Sabemos que un incendio no es solo daño material, es también estrés, incertidumbre y urgencia.
                </p>
                <p className="text-textLight text-lg leading-relaxed mb-6">
                  Por eso trabajamos con protocolos claros, equipamiento profesional y una comunicación constante. Nuestro objetivo es devolverte un espacio seguro, limpio y listo para que puedas seguir adelante.
                </p>
                <p className="text-textLight text-lg leading-relaxed">
                  Operamos en <a href="/#cobertura" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">Barcelona y toda el Área Metropolitana</a>, coordinando con seguros, peritos y administradores de fincas para hacer el proceso lo más sencillo posible.
                </p>
              </div>

              {/* Right Image */}
              <div className="lg:col-span-3">
                <div className="relative rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="https://public.readdy.ai/ai/img_res/ad3ee8fc5c7349053107507095e17f79.jpg"
                    alt="Equipo FIRETRAUMAS"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-6 left-6 bg-white rounded-lg px-6 py-3 shadow-md">
                    <p className="font-sans font-semibold text-primary">
                      Empresa local · Sarrià-Sant Gervasi
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Grid */}
        <section className="py-20 bg-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif font-bold text-4xl md:text-5xl text-primary mb-6">
                Nuestros valores
              </h2>
              <p className="text-textLight text-lg max-w-2xl mx-auto">
                Lo que nos define y guía nuestro trabajo cada día.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-secondary/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${value.icon} text-3xl text-secondary`}></i>
                  </div>
                  <h3 className="font-sans font-bold text-xl text-primary mb-4">
                    {value.title}
                  </h3>
                  <p className="text-textLight leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif font-bold text-4xl text-primary mb-6">
                  ¿Por qué elegirnos?
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-sans font-bold text-lg text-primary mb-2">
                        Conocimiento del Territorio
                      </h4>
                      <p className="text-textLight">
                        Años trabajando en Barcelona nos han enseñado las particularidades de cada barrio, tipo de edificio y normativa local. Conoce más sobre <a href="/servicios" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">nuestros servicios especializados</a>.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-sans font-bold text-lg text-primary mb-2">
                        Coordinación con Seguros
                      </h4>
                      <p className="text-textLight">
                        Facilitamos toda la documentación necesaria para tu reclamación. Trabajamos con los principales peritos de Barcelona. Consulta las <a href="/faq#seguros" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">preguntas frecuentes sobre seguros</a>.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-sans font-bold text-lg text-primary mb-2">
                        Equipamiento Profesional
                      </h4>
                      <p className="text-textLight">
                        Aspiradores HEPA, productos específicos por material, EPIs certificados y protocolos de contención para evitar dispersión de partículas.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-sans font-bold text-lg text-primary mb-2">
                        Trato Humano
                      </h4>
                      <p className="text-textLight">
                        Entendemos que es un momento difícil. Te acompañamos con empatía, claridad y respeto en todo el proceso.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20cleaning%20specialist%20using%20HEPA%20vacuum%20in%20Barcelona%20apartment%20after%20fire%20safety%20gear%20restoration%20work&width=700&height=800&seq=about-work-002&orientation=portrait"
                  alt="Trabajo profesional"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="py-20 bg-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif font-bold text-4xl text-primary mb-6">
              Nuestro compromiso
            </h2>
            <p className="text-textLight text-lg leading-relaxed mb-8">
              Nos comprometemos a actuar con rapidez, profesionalidad y transparencia. Cada intervención es única, y adaptamos nuestro protocolo a las necesidades específicas de tu caso. No prometemos milagros, pero sí un trabajo serio, documentado y coordinado con todas las partes implicadas.
            </p>
            <p className="text-textLight text-lg leading-relaxed mb-8">
              Nuestro objetivo final es simple: que recuperes tu espacio de forma segura y puedas volver a tu vida normal lo antes posible. Descubre <a href="/servicios#proceso" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">cómo trabajamos paso a paso</a>.
            </p>
            <a
              href="/contacto"
              className="inline-block bg-secondary text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer"
            >
              Hablemos de Tu Caso
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-primary rounded-lg p-12 text-center">
              <h2 className="font-serif font-bold text-4xl text-white mb-6">
                ¿Necesitas ayuda ahora?
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                Contacta con nosotros para una evaluación sin compromiso. Te orientamos sobre los pasos a seguir.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="tel:+34690220944"
                  className="bg-white text-primary px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all inline-flex items-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  690 22 09 44
                </a>
                <a
                  href="https://wa.me/34690220944"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all inline-flex items-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <CookieBanner />
      <BackToTop />
    </div>
  );
}